import { cn } from '@core/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef } from 'react';

import { Spinner } from '../Spinner/Spinner';

const buttonVariants = cva(
  'relative inline-flex items-center justify-center border border-solid outline-none transition-all duration-150 ease-in-out focus-visible:ring-2 focus-visible:ring-offset-2',
  {
    variants: {
      theme: {
        'primary-fill':
          'border-transparent bg-Gray-black text-Gray-white hover:border-Gray-900 hover:bg-Gray-900 focus-visible:ring-Gray-black/50 disabled:border-Gray-100 disabled:bg-Gray-100 disabled:text-Gray-400',
        'primary-line':
          'border-Gray-black bg-inherit text-Gray-black hover:border-Gray-700 hover:text-Gray-700 focus-visible:ring-Gray-black/50 disabled:border-Gray-400 disabled:bg-Gray-white disabled:text-Gray-400',
        'primary-text':
          'border-transparent bg-inherit text-Gray-black hover:text-Gray-700 focus-visible:ring-Gray-black/50 disabled:border-transparent disabled:bg-inherit disabled:text-Gray-black',
        'secondary-fill':
          'border-B-500 bg-B-500 text-Gray-white hover:border-B-600 hover:bg-B-600 focus-visible:ring-B-500/50 disabled:border-Gray-100 disabled:bg-Gray-100 disabled:text-Gray-400',
        'secondary-line':
          'border-B-500 bg-inherit text-B-500 hover:border-B-400 hover:text-B-600 focus-visible:ring-B-500/50 disabled:border-Gray-400 disabled:bg-Gray-white disabled:text-Gray-400',
        'secondary-text':
          'border-transparent bg-inherit text-B-500 hover:text-B-400 focus-visible:ring-B-500/50 disabled:border-transparent disabled:bg-inherit disabled:text-B-500',
        'sub-fill':
          'border-Gray-200 bg-Gray-200 text-Gray-900 hover:border-Gray-100 hover:bg-Gray-100 focus-visible:ring-Gray-300/50 disabled:border-Gray-100 disabled:bg-Gray-100 disabled:text-Gray-400',
        'sub-line':
          'border-Gray-300 bg-inherit text-Gray-900 focus-visible:ring-Gray-300/50 disabled:border-Gray-400 disabled:bg-Gray-white disabled:text-Gray-400',
        'sub-text':
          'border-transparent bg-inherit text-Gray-800 hover:text-Gray-500 focus-visible:ring-Gray-300/50 disabled:border-transparent disabled:bg-inherit disabled:text-Gray-400',
      },
      dimensions: {
        H56: 'h-56 gap-6 font-700 typo-body3 active:scale-[0.98] motion-reduce:transform-none',
        H48: 'h-48 gap-6 font-700 typo-body3 active:scale-[0.98] motion-reduce:transform-none',
        H40: 'h-40 gap-4 font-700 typo-body4',
        H32: 'h-32 gap-4 font-700 typo-caption1',
        H26: 'h-26 gap-4 font-600 typo-caption2',
      },
      roundness: {
        rectangle: 'rounded-xsmall',
        capsule: 'rounded-pill',
      },
    },
    compoundVariants: [
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H56',
        className: 'px-24 py-16',
      },
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H48',
        className: 'px-24 py-12',
      },
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H40',
        className: 'px-16 py-10',
      },
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H32',
        className: 'px-16 py-7',
      },
      {
        theme: [
          'primary-fill',
          'primary-line',
          'secondary-fill',
          'secondary-line',
          'sub-fill',
          'sub-line',
        ],
        dimensions: 'H26',
        className: 'px-10 py-5',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H56',
        className: 'p-16',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H48',
        className: 'px-16 py-12',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H40',
        className: 'px-8 py-10',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H32',
        className: 'px-8 py-7',
      },
      {
        theme: ['primary-text', 'secondary-text', 'sub-text'],
        dimensions: 'H26',
        className: 'px-4 py-5',
      },
    ],
    defaultVariants: {
      theme: 'primary-fill',
      dimensions: 'H56',
      roundness: 'rectangle',
    },
  },
);

export interface ButtonProps
  extends
    ComponentPropsWithoutRef<'button'>,
    VariantProps<typeof buttonVariants> {
  children?: ReactNode;
  asChild?: boolean;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      theme,
      dimensions,
      roundness,
      type = 'button',
      children,
      asChild,
      loading,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    const spinnerSize =
      dimensions === 'H56' || dimensions === 'H48'
        ? 20
        : dimensions === 'H40'
          ? 16
          : 14;

    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({
            dimensions,
            theme,
            roundness,
            className,
          }),
          {
            'opacity-50 !cursor-not-allowed': isDisabled,
          },
        )}
        type={type}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <span className="invisible contents">{children}</span>
            <span className="absolute">
              <Spinner size={spinnerSize} />
            </span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);

Button.displayName = 'Button';
